/**
 * Date Utilities for Film Tracking
 * Handles date calculations and formatting for API requests
 */

/**
 * Get default start date (14 days before current day)
 * @returns {string} - Date in YYYY-MM-DD format
 */
export const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 14);
  return date.toISOString().split('T')[0];
};

/**
 * Get default end date (42 days after current day)
 * @returns {string} - Date in YYYY-MM-DD format
 */
export const getDefaultEndDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 42);
  return date.toISOString().split('T')[0];
};

/**
 * Format date to YYYY-MM-DD format for API requests
 * @param {Date|string} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDateForAPI = (date) => {
  if (!date) return null;
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date provided to formatDateForAPI:', date);
    return null;
  }
  
  return dateObj.toISOString().split('T')[0];
};

/**
 * Parse date from API response (handles both string and object formats)
 * @param {string|Object} dateValue - Date value from API
 * @returns {Date|null} - Parsed Date object or null
 */
export const parseDateFromAPI = (dateValue) => {
  if (!dateValue) return null;
  
  // Handle object format like {value: "2025-09-05"}
  const dateString = typeof dateValue === 'object' && dateValue.value 
    ? dateValue.value 
    : dateValue;
    
  if (typeof dateString !== 'string') return null;
  
  const parsedDate = new Date(dateString);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

/**
 * Calculate weeks until release from current date
 * @param {string|Date} releaseDate - Release date
 * @returns {number} - Weeks until release (negative if past)
 */
export const getWeeksUntilRelease = (releaseDate) => {
  const release = parseDateFromAPI(releaseDate);
  if (!release) return 0;
  
  const now = new Date();
  const diffTime = release.getTime() - now.getTime();
  const diffWeeks = Math.round(diffTime / (1000 * 60 * 60 * 24 * 7));
  
  return diffWeeks;
};

/**
 * Format date for display in UI
 * @param {string|Date|Object} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string
 */
export const formatDateForDisplay = (date, options = {}) => {
  const parsedDate = parseDateFromAPI(date);
  if (!parsedDate) return 'N/A';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return parsedDate.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Get quick filter date ranges
 * @returns {Object} - Object with predefined date ranges
 */
export const getQuickFilterRanges = () => {
  const today = new Date();
  
  return {
    thisWeek: {
      label: 'This Week',
      startDate: formatDateForAPI(today),
      endDate: (() => {
        const end = new Date(today);
        end.setDate(end.getDate() + 7);
        return formatDateForAPI(end);
      })()
    },
    nextTwoWeeks: {
      label: 'Next 2 Weeks',
      startDate: formatDateForAPI(today),
      endDate: (() => {
        const end = new Date(today);
        end.setDate(end.getDate() + 14);
        return formatDateForAPI(end);
      })()
    },
    nextMonth: {
      label: 'Next Month',
      startDate: formatDateForAPI(today),
      endDate: (() => {
        const end = new Date(today);
        end.setDate(end.getDate() + 30);
        return formatDateForAPI(end);
      })()
    },
    lastTwoWeeks: {
      label: 'Last 2 Weeks',
      startDate: (() => {
        const start = new Date(today);
        start.setDate(start.getDate() - 14);
        return formatDateForAPI(start);
      })(),
      endDate: formatDateForAPI(today)
    },
    default: {
      label: 'Default Range',
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate()
    }
  };
};

/**
 * Validate date range
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Object} - Validation result with isValid and error message
 */
export const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return { isValid: false, error: 'Both start and end dates are required' };
  }
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { isValid: false, error: 'Invalid date format' };
  }
  
  if (start > end) {
    return { isValid: false, error: 'Start date must be before end date' };
  }
  
  // Check if range is too large (more than 1 year)
  const diffTime = end.getTime() - start.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  
  if (diffDays > 365) {
    return { isValid: false, error: 'Date range cannot exceed 1 year' };
  }
  
  return { isValid: true, error: null };
};

const dateUtils = {
  getDefaultStartDate,
  getDefaultEndDate,
  formatDateForAPI,
  parseDateFromAPI,
  getWeeksUntilRelease,
  formatDateForDisplay,
  getQuickFilterRanges,
  validateDateRange
};

export default dateUtils;
